/**
 * Workshop mailbox copy address (#3735).
 *
 * The workshop's own mailbox stays where it is; a routing rule in its mail
 * service adds this address as an extra recipient, and each email becomes a
 * conversation in Messages. Hidden until the workshop has the mailbox copy
 * switched on (`settings.mailboxInbox.enabled`), which the API reports as
 * `enabled`.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  useInboundEmailAddress,
  useRotateInboundEmailAddress,
} from '@/hooks/useInboundDocuments';
import { useNotify } from '@/hooks/useNotify';
import { InboundAddressDetails } from './InboundAddressDetails';

const ADDRESS_GRACE_DAYS = 14;

export function MailboxCopySection({ canRotate }: { canRotate: boolean }) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const addressQuery = useInboundEmailAddress('mailbox');
  const rotateMutation = useRotateInboundEmailAddress('mailbox');
  const [confirmRotate, setConfirmRotate] = useState(false);

  const data = addressQuery.data;
  if (data?.enabled !== true || !data.configured || !data.address) return null;

  const handleRotate = async () => {
    try {
      await rotateMutation.mutateAsync();
      notify.success(t('inboundEmail.rotated'), t('inboundEmail.mailbox.rotatedDesc', { days: ADDRESS_GRACE_DAYS }));
      setConfirmRotate(false);
    } catch {
      notify.error(tc('error'), t('inboundEmail.rotateFailed'));
    }
  };

  return (
    <Stack spacing={3} data-testid="mailbox-copy-section">
      <Divider />
      <Box>
        <Typography variant="h6" fontWeight={600}>
          {t('inboundEmail.mailbox.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('inboundEmail.mailbox.description')}
        </Typography>
      </Box>

      <InboundAddressDetails
        address={data.address}
        graceAddresses={data.graceAddresses}
        label={t('inboundEmail.mailbox.addressLabel')}
        copyLabel={t('inboundEmail.mailbox.copyAddress')}
        testId="inbound-mailbox-address"
      />

      {canRotate && (
        <Box>
          <Button
            variant="outlined"
            onClick={() => setConfirmRotate(true)}
            disabled={rotateMutation.isPending}
          >
            {t('inboundEmail.mailbox.rotate')}
          </Button>
        </Box>
      )}

      <Alert severity="info">{t('inboundEmail.mailbox.routingInstructions')}</Alert>

      <ConfirmDialog
        open={confirmRotate}
        title={t('inboundEmail.rotateTitle')}
        message={t('inboundEmail.mailbox.rotateMessage', { days: ADDRESS_GRACE_DAYS })}
        confirmText={t('inboundEmail.mailbox.rotate')}
        onConfirm={() => {
          void handleRotate();
        }}
        onCancel={() => setConfirmRotate(false)}
        loading={rotateMutation.isPending}
      />
    </Stack>
  );
}
